import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slidesContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class CartUpsell extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #startX = 0;
  #isDragging = false;
  #wheelLocked = false;
  #wheelTimeout = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    //  Drag events
    this.addEventListener('mousedown', this.#onDragStart);
    this.addEventListener('touchstart', this.#onDragStart, { passive: true });

    this.addEventListener('mousemove', this.#onDragMove);
    this.addEventListener('touchmove', this.#onDragMove, { passive: true });

    this.addEventListener('mouseup', this.#onDragEnd);
    this.addEventListener('mouseleave', this.#onDragEnd);
    this.addEventListener('touchend', this.#onDragEnd);
    this.addEventListener('wheel', this.#onWheel, { passive: false });

    //  Dots
    this.refs.dots?.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.current = index;
      });
    });

    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

set current(current) {
  this.#current = current;

  const slides = this.refs.slides ?? [];
  const total = slides.length;
  if (!slides.length) return;

  const slideWidth = slides[0].offsetWidth;
  const gap = parseInt(getComputedStyle(slides[0]).marginRight) || 26;
  const containerWidth = this.refs.slidesContainer.offsetParent.offsetWidth; // visible slider width
  const totalWidth = total * (slideWidth + gap) - gap; // total width of all slides

  let offset = this.#current * (slideWidth + gap);

  // Clamp offset so last slide aligns with container width
  const maxOffset = Math.max(totalWidth - containerWidth, 0);
  offset = Math.min(offset, maxOffset);

  this.refs.slidesContainer.style.transform = `translateX(-${offset}px)`;

  // Update dots
  this.refs.dots?.forEach((dot, index) => {
    dot.toggleAttribute('active', index === this.#current);
  });
}

  #onDragStart = (e) => {
  this.#isDragging = true;
  this.#startX = e.touches ? e.touches[0].clientX : e.clientX;
  this.refs.slidesContainer.style.transition = 'none';
};

#onDragMove = (e) => {
  if (!this.#isDragging) return;

  const currentX = e.touches ? e.touches[0].clientX : e.clientX;
  const diff = currentX - this.#startX;

  const slides = this.refs.slides ?? [];
  if (!slides.length) return;

  const slideWidth = slides[0].offsetWidth;
  const gap = parseInt(getComputedStyle(slides[0]).marginRight) || 26;
  const containerWidth = this.refs.slidesContainer.offsetParent.offsetWidth;
  const totalWidth = slides.length * (slideWidth + gap) - gap;

  let newPosition = -this.#current * (slideWidth + gap) + diff;

  // Clamp min/max so last slide sticks to container
  const maxTranslate = 0;
  const minTranslate = Math.min(containerWidth - totalWidth, 0); 
  newPosition = Math.min(maxTranslate, Math.max(minTranslate, newPosition));

  this.refs.slidesContainer.style.transform = `translateX(${newPosition}px)`;
};

#onDragEnd = (e) => {
  if (!this.#isDragging) return;

  const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
  const diff = endX - this.#startX;

  const slides = this.refs.slides ?? [];
  const maxIndex = slides.length - 1;

  const threshold = 50; // px

  if (diff > threshold && this.#current > 0) this.previous();   // can't go left past 0
  else if (diff < -threshold && this.#current < maxIndex) this.next(); // can't go right past last
  else this.current = this.#current; // snap back if small drag

  this.refs.slidesContainer.style.transition = 'transform 0.5s ease';
  this.#isDragging = false;
};

#onWheel = (e) => {
    const slides = this.refs.slides ?? [];
  if (!slides.length) return;

  const maxIndex = slides.length - 1;

  const scrollingDown = e.deltaY > 0;
  const scrollingUp = e.deltaY < 0;

  const atStart = this.#current === 0;
  const atEnd = this.#current === maxIndex;

  if ((scrollingUp && atStart) || (scrollingDown && atEnd)) {
    return; 
  }

  e.preventDefault();

  if (Math.abs(e.deltaY) < 10 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
  if (this.#wheelLocked) return;

  this.#wheelLocked = true;

  if (scrollingDown) this.next();
  else this.previous();

  clearTimeout(this.#wheelTimeout);
  this.#wheelTimeout = setTimeout(() => {
    this.#wheelLocked = false;
  }, 300);
};

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsell);
}
